import { useEffect, useState } from 'react'
import {
  attendancePersonalistaReports,
  downloadAttendanceAvensioExport,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'
import { AttendanceAbsenceReasonsPanel } from './AttendanceAbsenceReasonsPanel'
import { AttendanceBankaPanel } from './AttendanceBankaPanel'
import { AttendanceEventTypesPanel } from './AttendanceEventTypesPanel'
import { AttendanceHolidaysPanel } from './AttendanceHolidaysPanel'
import { AttendanceLeadersAdminPanel } from './AttendanceLeadersAdminPanel'
import { AttendanceLeaveBalancesPanel } from './AttendanceLeaveBalancesPanel'
import { AttendanceRegimesAdminPanel } from './AttendanceRegimesAdminPanel'
import { AttendanceShiftsPanel } from './AttendanceShiftsPanel'
import { AttendanceWorkSchedulesPanel } from './AttendanceWorkSchedulesPanel'

export const ATT_HR_SECTIONS = [
  ['zustatky', 'Nároky a čerpání'],
  ['banka', 'Banka hodin'],
  ['nepritomnosti', 'Nepřítomnosti'],
  ['udalosti', 'Události'],
  ['smeny', 'Směny'],
  ['rozvrhy', 'Pozice'],
  ['rezimy', 'Přiřazení na pozici'],
  ['svatky', 'Kalendář svátků'],
  ['vedouci', 'Vedoucí'],
  ['sestavy', 'Sestavy'],
  ['avensio', 'Avensio export'],
] as const

export type AttendanceHrSection = (typeof ATT_HR_SECTIONS)[number][0]

export function isAttendanceHrSection(v: string | undefined): v is AttendanceHrSection {
  return ATT_HR_SECTIONS.some(([k]) => k === v)
}

type Props = {
  section: AttendanceHrSection
  year: number
  month: number
  busy: boolean
  setBusy: (v: boolean) => void
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  onReloadPrehled?: () => void | Promise<void>
}

export function AttendanceHrExtrasPanel({
  section,
  year,
  month,
  busy,
  setBusy,
  showAlert,
  onReloadPrehled,
}: Props) {
  const [reportKind, setReportKind] = useState<'weekends' | 'nights' | 'late' | 'bank_year'>('weekends')
  const [reportRows, setReportRows] = useState<Array<Record<string, unknown>>>([])

  useEffect(() => {
    if (section !== 'sestavy') return
    let cancelled = false
    attendancePersonalistaReports(reportKind, year, reportKind === 'bank_year' ? undefined : month)
      .then((r) => {
        if (!cancelled) setReportRows(Array.isArray(r.rows) ? r.rows : [])
      })
      .catch((e: unknown) => {
        if (!cancelled) showAlert(e instanceof Error ? e.message : 'Sestava selhala', 'error')
      })
    return () => {
      cancelled = true
    }
  }, [section, reportKind, year, month, showAlert])

  const reloadCatalog = () => {
    void onReloadPrehled?.()
  }

  if (section === 'zustatky') {
    return (
      <AttendanceLeaveBalancesPanel
        year={year}
        busy={busy}
        setBusy={setBusy}
        showAlert={showAlert}
      />
    )
  }

  if (section === 'banka') {
    return (
      <AttendanceBankaPanel
        year={year}
        month={month}
        busy={busy}
        setBusy={setBusy}
        showAlert={showAlert}
      />
    )
  }

  if (section === 'nepritomnosti') {
    return (
      <AttendanceAbsenceReasonsPanel showAlert={showAlert} embedded onChanged={reloadCatalog} />
    )
  }

  if (section === 'udalosti') {
    return <AttendanceEventTypesPanel showAlert={showAlert} embedded />
  }

  if (section === 'smeny') {
    return <AttendanceShiftsPanel showAlert={showAlert} embedded onChanged={reloadCatalog} />
  }

  if (section === 'rozvrhy') {
    return <AttendanceWorkSchedulesPanel showAlert={showAlert} embedded onChanged={reloadCatalog} />
  }

  if (section === 'rezimy') {
    return <AttendanceRegimesAdminPanel showAlert={showAlert} />
  }

  if (section === 'svatky') {
    return <AttendanceHolidaysPanel showAlert={showAlert} embedded initialYear={year} />
  }

  if (section === 'vedouci') {
    return <AttendanceLeadersAdminPanel showAlert={showAlert} />
  }

  if (section === 'sestavy') {
    return (
      <div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {(
            [
              ['weekends', 'Víkendy'],
              ['nights', 'Noční'],
              ['late', 'Pozdní příchody'],
              ['bank_year', 'Banka (rok)'],
            ] as const
          ).map(([k, label]) => (
            <button key={k} type="button" disabled={busy} onClick={() => setReportKind(k)}>
              {reportKind === k ? `• ${label}` : label}
            </button>
          ))}
        </div>
        <div className="att-cat__wrap">
          <table className="att-cat__table">
            <thead>
              <tr>
                <th className="att-cat__col-name att-cat__col-name--person">Zaměstnanec</th>
                <th className="att-cat__col-narrow">Os. č.</th>
                <th className="att-cat__col-week att-cat__col-fill">Údaje</th>
              </tr>
            </thead>
            <tbody>
              {reportRows.map((r, i) => (
                <tr key={String(r.employee_id ?? i)}>
                  <td className="att-cat__col-name att-cat__col-name--person">
                    {String(r.name ?? '')}
                  </td>
                  <td className="att-cat__col-narrow">{String(r.personal_number ?? '—')}</td>
                  <td className="att-cat__col-week att-cat__week">
                    {reportKind === 'weekends'
                      ? `So ${r.saturday_days}× (${r.saturday_label}), Ne ${r.sunday_days}× (${r.sunday_label})`
                      : reportKind === 'nights'
                        ? `${r.night_label} (${r.window})`
                        : reportKind === 'late'
                          ? `${r.late_days} dnů / ${r.late_label}`
                          : `${r.balance_label} (rok ${r.year_delta_label})`}
                  </td>
                </tr>
              ))}
              {reportRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="att-cat__empty">
                    Žádné záznamy.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (section === 'avensio') {
    return (
      <div>
        <p className="attendance-muted">
          Export ve formátu Beta 3.8 (soubory D*.CCC + P*.CCC v ZIP). Výchozí: jen uzavřené výkazy.
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            void (async () => {
              setBusy(true)
              try {
                await downloadAttendanceAvensioExport({
                  year,
                  month,
                  closed_only: true,
                  file: 'both',
                })
                showAlert('Avensio ZIP stažen', 'success')
              } catch (e: unknown) {
                showAlert(e instanceof Error ? e.message : 'Export selhal', 'error')
              } finally {
                setBusy(false)
              }
            })()
          }}
        >
          Stáhnout Avensio ZIP ({month}/{year})
        </button>
      </div>
    )
  }

  return null
}
